/**
 * @file userService.js
 * @description APIs for User related requests.
 **/

import { http, userApiHttp } from './httpService';

const getCompanyCode = () => window.localStorage.getItem('companyCode');

/**
 * Retrieves the anonymous user onboarding lookup data.
 *
 * @param {string} dateOfBirth - The date of birth of the user in the format "YYYY-MM-DD".
 * @param {string} employeeId - The employee ID of the user.
 * @param {string} lastName - The last name of the user.
 * @param {string} namespace - The namespace for the configuration data.
 * @returns {Promise<object | null>} A promise that resolves to the anonymous user onboarding lookup data.
 */
export const getAnonymousUserOnboardingLookup = async (
    identifiers,
    namespace,
    tenantId,
) => {
    try {
        const response = await userApiHttp.post(
            `/v2/user/onboarding/anonymous?tenantId=${tenantId}&companyCode=${namespace}`,
            identifiers,
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error(
            'Error retrieving anonymous user onboarding lookup data:',
            error,
        );
        throw error;
    }
};

/**
 * Retrieves the onboarding details for a user by onboarding code.
 *
 * @param {string} onboardingCode - The unique onboarding code for the user.
 * @param {string} namespace - The namespace for the configuration data.
 * @returns {Promise<object | null>} - A promise that resolves to the user onboarding details.
 */
export const getOnboardingDetails = async (onboardingCode, namespace) => {
    try {
        const response = await userApiHttp.get(
            `/v2/user/onboarding/${onboardingCode}`,
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving onboarding details:', error);
        throw error;
    }
};

/**
 * Finalizes the onboarding process by submitting user credentials and communication preferences.
 *
 * @param {string} onboardingCode - The onboarding code of the user.
 * @param {string} namespace - The company code for the user.
 * @param {string} username - The chosen username for the user.
 * @param {string} password - The chosen password for the user.
 * @param {string} mobilePhoneNumber - The user's mobile phone number.
 * @param {string} email - The user's email address.
 * @returns {Promise<object | null>} A promise that resolves to the finalization status.
 */
export const onboardUser = async (
    onboardingCode,
    namespace,
    username,
    password,
    mobilePhoneNumber,
    email,
    comm_preference = 'email', // email sms or push
    notification_preferred_method_id,
) => {
    try {
        const payload = {
            username,
            password,
            mobile_phone_number: mobilePhoneNumber?.length
                ? mobilePhoneNumber
                : null,
            email,
            android_app_link: null,
            ios_app_link: null,
            comm_preference,
            notification_preferred_method_id,
            // ...(mobilePhoneNumber && { mobile_phone_number: mobilePhoneNumber })
        };

        const response = await userApiHttp.post(
            `/v2/user/onboarding/${onboardingCode}/onboard?namespace=${namespace}`,
            payload,
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error finalizing user onboarding:', error);
        throw error;
    }
};

/**
 * Retrieves the current user's data.
 *
 * @returns {Promise<object>} A promise that resolves to the current user's data.
 */
export const getCurrentUser = async () => {
    try {
        const response = await userApiHttp.get('/v2/user/current', {
            skipTenantHeader: true,
        });
        return response?.data?.users[0] || null;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        throw error;
    }
};

/**
 * Changes the current user's password.
 *
 * @returns {Promise<object>} A promise that resolves to the password change status.
 */
export const changePassword = async (currentPassword, newPassword) => {
    try {
        let device_id = localStorage.getItem('deviceID');
        if (!device_id) {
            device_id = uuidv4();
            localStorage.setItem('deviceID', device_id);
        }

        const payload = {
            old_password: currentPassword,
            new_password: newPassword,
            device_id: device_id,
        };

        const response = await userApiHttp.post(
            '/v2/user/current/reset_password',
            payload,
            { skipTenantHeader: true },
        );
        return response?.status || null;
    } catch (error) {
        console.error('Error changing password:', error);
        throw error;
    }
};

/**
 * Updates the user's MFA method.
 *
 * @param {string} token - The token to confirm the new mfa method for the user.
 * @returns {Promise<Object>} - The response data.
 * @throws {Error} - If there is an error with the confirm mfa request.
 */
export const confirmMFA = async token => {
    try {
        const response = await http.get(
            `/v2/user/current/confirm_mfa?token=${token}`,
            {},
            { skipTenantHeader: true },
        );
        return response.status;
    } catch (error) {
        console.error('Error confirming MFA:', error);
    }
};

/**
 * Updates the user's settings.
 *
 * @param {string} locale - The new locale value the user wants to set.
 * @param {string} preferredName - The new preferred name value the user wants to set.
 * @param {string} pronouns - The new pronouns value the user wants to set.
 * @returns {Promise<Object>} - The response data.
 * @throws {Error} - If there is an error with the update user settings request.
 */
export const updateUserSettings = async (
    locale,
    preferredName,
    pronouns,
    avatarURL,
    username,
) => {
    try {
        const response = await userApiHttp.put(
            '/v2/user/current/settings',
            {
                locale: locale,
                preferred_name: preferredName,
                pronouns: pronouns,
                avatar_url: avatarURL,
                username: username,
            },
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error updating user settings:', error);
        throw error;
    }
};

/**
 * Retrieves the authenticated user's email.
 *
 * @returns {Promise<object>} A promise that resolves to the user's email data.
 */
export const getUserAuthEmail = async (identifiers, companyCode) => {
    try {
        if (!companyCode) companyCode = getCompanyCode();
        const response = await userApiHttp.post(
            `/v2/user/forgot?companyCode=${companyCode}`,
            identifiers,
            { skipTenantHeader: true },
        );

        return response.data;
    } catch (error) {
        console.error('Error retrieving user email:', error);
        throw error;
    }
};

/**
 * Update the user's messaging token for current session.
 *
 * @param {string} token - The FCM token
 * @returns {Promise<Object>} - The response data.
 * @throws {Error} - If there is an error with the update user settings request.
 */
export const registerFCMToken = async token => {
    console.log('sending FCM token to backend');
    try {
        let deviceID = localStorage.getItem('deviceID');
        if (!deviceID) {
            deviceID = uuidv4();
            localStorage.setItem('deviceID', deviceID);
        }

        const response = await http.post(
            '/v2/user/messaging/register/gcm',
            {
                token,
                device_id: deviceID,
            },
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error(
            'Error updating user messaging token for current device session:',
            error,
        );
        throw error;
    }
};

/**
 * Retrieves all additional users.
 *
 * @returns {Promise<object[]>} A promise that resolves to the list of additional users.
 */
export async function getAdditionalUsers() {
    try {
        const response = await userApiHttp.get('/v2/user/additional', {
            skipTenantHeader: true,
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching additional users:', error);
        throw error;
    }
}

/**
 * Creates a new additional user.
 *
 * @param {Object} userData - The payload for the new additional user.
 * @returns {Promise<object>} A promise that resolves to the created user data.
 */
export async function createAdditionalUser(userData) {
    try {
        const response = await userApiHttp.post(
            '/v2/user/additional',
            userData,
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error creating additional user:', error);
        throw error;
    }
}

/**
 * Deletes an additional user by ID.
 *
 * @param {string} userId - The ID of the additional user to delete.
 * @returns {Promise<number>} A promise that resolves to the HTTP status code.
 */
export async function deleteAdditionalUser(userId) {
    try {
        const response = await userApiHttp.delete(
            `/v2/user/additional/${userId}`,
            { skipTenantHeader: true },
        );
        return response.status;
    } catch (error) {
        console.error(
            `Error deleting additional user with id ${userId}:`,
            error,
        );
        throw error;
    }
}

/**
 * Retrieves a specific additional user by ID.
 *
 * @param {string|number} userId - The ID of the additional user to fetch.
 * @returns {Promise<object|null>} A promise that resolves to the additional user data.
 */
export async function getAdditionalUser(userId) {
    try {
        const response = await userApiHttp.get(
            `/v2/user/additional/${userId}`,
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error fetching additional user with id ${userId}:`,
            error,
        );
        throw error;
    }
}

/**
 * Mock data for an additional user (useful for development or tests).
 *
 * @param {string|number} [userId] - Optional user id (ignored by mock).
 * @returns {Promise<object>} A promise that resolves to mock additional user data.
 */
export async function getAdditionalUserMock(userId) {
    return {
        id: 1,
        first_name: 'Asher',
        middle_name: 'M',
        last_name: 'Adams',
        email_address: 'orangeinavdon@yahoo.com',
        mobile_phone_number: null,
        relationship: {
            id: 2,
            name: 'Child',
        },
        status: 'active',
        access: {
            benefits: true,
            portals: true,
            private_assets: false,
            single_sign_on: true,
        },
        invitation: {
            sent_at: '2025-11-21T11:45:00Z',
            expires_at: '2025-11-22T11:45:00Z',
            status: 'pending',
        },
    };
}

/**
 * Updates an existing additional user.
 *
 * @param {string|number} userId - The ID of the additional user to update.
 * @param {Object} userData - The payload for updating the additional user.
 * @returns {Promise<object>} A promise that resolves to the updated user data.
 */
export async function updateAdditionalUser(userId, userData) {
    try {
        const response = await userApiHttp.put(
            `/v2/user/additional/${userId}`,
            userData,
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error updating additional user with id ${userId}:`,
            error,
        );
        throw error;
    }
}

/**
 * Validates self-registration data before final submission
 *
 * @param {object} profileData - User profile data
 * @param {string} companyCode - Company code
 * @returns {Promise<object>} Validation result
 * @throws {Error} - If validation fails
 */
export const validateSelfRegistration = async (profileData, companyCode) => {
    try {
        if (!companyCode) companyCode = getCompanyCode();
        const payload = {
            unique_id: profileData.uniqueId || '',
            first_name: profileData.firstName,
            last_name: profileData.lastName,
            email_address: profileData.email || null,
            mobile_phone_number: profileData.mobilePhoneNumber || null,
            identifiers: profileData.identifiers || [],
            validate: true,
        };

        const response = await userApiHttp.post(
            `/v2/user/onboarding/onboard?companyCode=${companyCode}`,
            payload,
            {
                skipTenantHeader: true,
            },
        );
        return response.data;
    } catch (error) {
        console.error('Error validating self-registration:', error);
        throw error;
    }
};

/**
 * Submits final self-registration data
 *
 * @param {object} profileData - User profile data
 * @param {string} password - User password
 * @param {object} notificationPreferences - Notification preferences containing communicationPreferences
 * @param {string} companyCode - Company code
 * @returns {Promise<object>} Registration result
 * @throws {Error} - If submission fails
 */
export const submitSelfRegistration = async (
    profileData,
    password,
    notificationPreferences,
    companyCode,
) => {
    try {
        if (!companyCode) companyCode = getCompanyCode();

        const commPrefs =
            notificationPreferences.communicationPreferences || {};

        const payload = {
            unique_id: profileData.uniqueId || '',
            first_name: profileData.firstName,
            last_name: profileData.lastName,
            email_address: commPrefs.email || profileData.email || null,
            mobile_phone_number:
                commPrefs.mobilePhoneNumber ||
                profileData.mobilePhoneNumber ||
                null,
            password: password,
            identifiers: profileData.identifiers || [],
            validate: false,
        };

        const response = await userApiHttp.post(
            `/v2/user/onboarding/onboard?companyCode=${companyCode}`,
            payload,
            {
                skipTenantHeader: true,
            },
        );
        return response.data;
    } catch (error) {
        console.error('Error submitting self-registration:', error);
        throw error;
    }
};

/**
 * Retrieves the effective EmailAddress field label for unauthenticated onboarding.
 *
 * @param {string} companyCode - The tenant namespace.
 * @returns {Promise<string | null>} The configured email address label, if available.
 */
export const getAnonymousEmailAddressLabel = async companyCode => {
    if (!companyCode) {
        return null;
    }

    try {
        const response = await userApiHttp.get(
            `/v2/user/onboarding/email-address-label?companyCode=${encodeURIComponent(
                companyCode,
            )}`,
            { skipTenantHeader: true },
        );

        return response.data || null;
    } catch (error) {
        console.error('Error retrieving anonymous email address label:', error);
        return null;
    }
};
